/**
 * 018: COPIED from
 *   wrapper: vendor/isomorphic/apps/isomorphic/src/app/shared/auth-layout/auth-wrapper-one.tsx
 *   with the field markup of vendor/isomorphic/apps/isomorphic/src/app/auth/(sign-in)/sign-in-1/
 *   sign-in-form.tsx
 *
 * The template ships no dedicated "reset complete" page among its five forgot-password variants —
 * this is a confirmation body inside the same wrapper, banner column and hero image as the other
 * four auth screens, not a page the template names. The field is a rizzui `Password` with the
 * template's own size, label and input classes, wrapped in the template's own `Form`
 * (@core/ui/form.tsx) — the same composition as sign-in.tsx in this folder.
 *
 * Only the data, labels, routes and business rules are ours.
 */
import { Link, useNavigate, useParams } from "react-router-dom";
import type { SubmitHandler } from "react-hook-form";
import { z } from "zod";
import toast from "react-hot-toast";
import { Button, Password, Text } from "rizzui";
import { AuthWrapperOne, UnderlineShape, Form } from "@steelyard/theme";
import { completePasswordReset } from "../../lib/identity";
import heroImage from "../../assets/auth/sign-in-hero.svg";

/**
 * Ported from apps/admin/app/(auth)/password-reset/[token]/page.tsx. The token is not checked
 * before the form is shown — a dead link reports itself on submission, back on the request
 * screen (`?refused=`), same as the Next version. No session is written on success.
 */
const newPasswordSchema = z.object({ newPassword: z.string().min(1, "New password is required") });
type NewPasswordValues = z.infer<typeof newPasswordSchema>;

export default function PasswordResetCompletePage() {
  const { token = "" } = useParams();
  const navigate = useNavigate();

  const onSubmit: SubmitHandler<NewPasswordValues> = async (values) => {
    try {
      const { refused } = await completePasswordReset(token, values.newPassword);
      if (refused) return navigate(`/password-reset?refused=${refused}`);
      navigate("/sign-in?reset=done");
    } catch {
      toast.error("Could not set the new password. Try again.");
    }
  };

  return (
    <AuthWrapperOne
      title={
        <>
          Choose a{" "}
          <span className="relative inline-block">
            new password.
            <UnderlineShape className="absolute -bottom-2 end-0 h-2.5 w-32 text-blue xl:-bottom-1.5 xl:w-40" />
          </span>
        </>
      }
      bannerTitle="Infra Junction's operations, in one back office."
      bannerDescription="Orders, credit, GST compliance and warehouse dispatch — the same system your colleagues use, kept current with what the API actually returns."
      isSocialLoginActive={false}
      pageImage={
        <div className="relative mx-auto aspect-[4/3.37] h-[calc(100vh-31rem)] max-w-full">
          <img src={heroImage} alt="" className="absolute inset-0 size-full rounded-lg object-contain" />
        </div>
      }
    >
      <Text className="-mt-4 mb-6 text-center leading-loose text-gray-500 lg:text-start">
        This link can be used once.
      </Text>
      <Form<NewPasswordValues>
        validationSchema={newPasswordSchema}
        onSubmit={onSubmit}
        useFormProps={{ defaultValues: { newPassword: "" } }}
      >
        {({ register, formState: { errors, isSubmitting } }) => (
          <div className="space-y-5">
            <Password
              label="New password"
              size="lg"
              className="[&>label>span]:font-medium"
              inputClassName="text-sm"
              autoFocus
              autoComplete="new-password"
              helperText="The service decides what it will accept and says so if it refuses."
              error={errors.newPassword?.message}
              {...register("newPassword")}
            />
            <Button type="submit" className="w-full" size="lg" isLoading={isSubmitting}>
              Set the password
            </Button>
          </div>
        )}
      </Form>

      <Text className="mt-6 text-center leading-loose text-gray-500 lg:mt-8 lg:text-start">
        <Link to="/sign-in" className="font-semibold text-gray-700 transition-colors hover:text-blue">Back to sign in</Link>
      </Text>
    </AuthWrapperOne>
  );
}
